import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { planDocumentAPI } from '../../services/api'
import { PlanMenuCards, PlanWeekendCards } from './PlanCards'
import WeeklyPlanView from './WeeklyPlanView'
import { fmtDayRange } from './planFormat'
import { contentLanguage } from '../../i18n/supported'

const uiLanguage = contentLanguage

function Heading({ eyebrow, title }) {
  return (
    <div className="flex flex-col gap-0.5 border-b-2 border-[#14181A] pb-2 mb-3 font-sans">
      <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-[#8B959C]">{eyebrow}</span>
      <h5 className="text-[18px] font-semibold tracking-tight text-[#14181A]">{title}</h5>
    </div>
  )
}

// VH-229 refinement: a cycle week shown directly as its plan — Mon–Fri menu cards and the weekend
// proposal cards with max calories; the full document (shopping, cooking day, shelf life, changes)
// one click away. Weeks without a document render nothing here: the week panel offers copy / new plan.
export function WeekPlanSummary({ cycleId, planId, weekStart }) {
  const { t, i18n } = useTranslation()
  const [showFull, setShowFull] = useState(false)
  const language = uiLanguage(i18n.language)
  const query = useQuery({
    queryKey: ['planDocument', cycleId, planId, language],
    queryFn: () => planDocumentAPI.get(cycleId, planId, language).then((r) => r.data),
    enabled: !!cycleId && !!planId,
    retry: (count, err) => err?.response?.status !== 404 && count < 1,
  })
  const doc = query.data?.document

  if (query.isLoading) return <p className="text-sm text-gray-500" role="status">{t('weeklyPlan.loading')}</p>
  if (!doc) return null

  const containers = doc.header?.containers ?? 5
  const hasWeekend = (doc.weekend?.days ?? []).length > 0 || (doc.weekend?.rows ?? []).length > 0
  return (
    <div className="space-y-6" data-testid="week-plan-summary">
      <div className="rounded-lg bg-[#F6F7F4] p-3 sm:p-4 space-y-6">
        <section data-testid="week-plan-menu">
          <Heading eyebrow={`${containers === 5 ? t('weeklyPlan.menu.eyebrowWeekdays') : t('weeklyPlan.menu.eyebrow', { count: containers })} · ${fmtDayRange(weekStart, containers, i18n.language)}`}
            title={t('weeklyPlan.menu.title')} />
          <PlanMenuCards document={doc} />
        </section>
        {hasWeekend && (
          <section data-testid="week-plan-weekend">
            <Heading eyebrow={t('weeklyPlan.weekend.eyebrow')} title={t('weeklyPlan.weekend.budgetTitle')} />
            <PlanWeekendCards document={doc} />
          </section>
        )}
      </div>
      <div>
        <button type="button" className="btn-outline text-sm" aria-expanded={showFull} onClick={() => setShowFull((v) => !v)}
          data-testid="toggle-full-document">
          {showFull ? t('weeklyPlan.hideFullDocument') : t('weeklyPlan.showFullDocument')}
        </button>
      </div>
      {showFull && <WeeklyPlanView cycleId={cycleId} planId={planId} weekStart={weekStart} />}
    </div>
  )
}

export default WeekPlanSummary
